import { IObservableValue, observable } from 'mobx';
import GuestLoginStore from '@app/guestLogin/stores/GuestLoginStore';
import request, { HttpMethod, HttpStatus } from '@app/libs/request';
import config, { serverUrl } from '@app/config';
import { guestHeaderGenerator } from '@app/utils/userIdentityHeadersGenerator';
import Log from '@app/libs/logger';
import { UUID_REGEX } from '@app/utils/regexUtils';

// Also the i18n namespace prefix these screens resolve their keys under (see
// public/locales/supplierRegistration/en/translation.json -> supplierValidation.guestLogin).
export const SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE = 'supplierValidation.guestLogin';

// GuestLoginStore's own credential storage (STORAGE_GUEST_AUTH_KEY = 'auth_guest') is shared by
// every registration link on this mode's origin -- ref/asyncId (see useRefFromLocation.ts /
// useAsyncIdFromLocation.ts) are the only things that distinguish one supplier's registration
// from another's, there is no per-registration path segment. This key remembers which
// registration a stored credential belongs to, so a *different* registration link on the same
// browser never silently inherits a previous supplier's verified identity.
const SR_GUEST_SESSION_CONTEXT_STORAGE_KEY = 'sr_guest_session_context';

interface GuestSessionResponseAttribute {
  type: string;
  value: string;
}

interface GuestSessionResponse {
  attributes?: GuestSessionResponseAttribute[];
}

function extractUuidQueryParam(rawValue: string | null): string | null {
  return rawValue && UUID_REGEX.test(rawValue) ? rawValue : null;
}

// Returns null when neither ref nor asyncId is present -- an un-identifiable registration can
// never be trusted to restore a previously-stored session (see the storage-key comment above).
function getCurrentRegistrationContextKey(): string | null {
  const params = new URLSearchParams(window.location.search);
  const ref = extractUuidQueryParam(params.get('ref'));
  const asyncId = extractUuidQueryParam(params.get('asyncId'));

  if (!ref && !asyncId) {
    return null;
  }

  return `${ref ?? ''}|${asyncId ?? ''}`;
}

// The Keycloak id token's exp claim in epoch millis. Undefined for anything that is not a readable
// JWT with a numeric exp, so an opaque or malformed token is left for the server to judge.
function getJwtExpirationMillis(token: string | undefined): number | undefined {
  const payloadSegment = token?.split('.')[1];

  if (!payloadSegment) {
    return undefined;
  }

  try {
    const payload = JSON.parse(atob(payloadSegment.replace(/-/g, '+').replace(/_/g, '/')));

    return typeof payload.exp === 'number' ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A GuestLoginStore whose `login()` performs the real OTP-token -> guest-session exchange:
 * GET /api/login/guest with an explicit Authorization-Guest header. The base implementation
 * relies on the app-wide dynamic-headers wiring (set up once per mode in InfraStores.init()),
 * which supplierRegistration's InfraStores never configures for guest headers -- this store
 * is a standalone, mode-independent session, not a full GuestInfraStores host.
 */
export class SupplierRegistrationGuestSessionStore extends GuestLoginStore {
  private readonly _verifiedEmail: IObservableValue<string | undefined> = observable.box<string | undefined>(undefined);
  private _initialized = false;
  // Set when the server accepts a token the device clock already calls expired: that clock runs
  // ahead of Keycloak, so for this session only the server (via the 401 path) judges expiry.
  private _trustServerOverLocalClock = false;

  constructor() {
    super(SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE);
  }

  get verifiedEmail(): string | undefined {
    return this._verifiedEmail.get();
  }

  // login() proves the token only at the moment of the exchange; the token's own exp claim is what
  // keeps this honest afterwards, so the pre-upload gate sends the supplier back to verification
  // instead of letting a multi-file upload fail on a header the gateway will reject. A device clock
  // ahead of the server shortens that window by the skew, never past the server's own verdict (see
  // _trustServerOverLocalClock). Time-based and therefore not reactive: the gates read it at
  // interaction time, and the 401 path clears the session (which is observable) for everything
  // rendered from it.
  get isVerified(): boolean {
    return !!this.verifiedEmail && (this._trustServerOverLocalClock || !this.isTokenExpiredByLocalClock());
  }

  private isTokenExpiredByLocalClock(): boolean {
    const expiresAt = getJwtExpirationMillis(this.guestLoginCredentials?.emailToken);

    return expiresAt !== undefined && expiresAt <= Date.now();
  }

  // Restores a previously-verified session from storage. Safe to call more than once (e.g. by
  // both the document-upload gate and the verification modal on mount) -- only the first call
  // does any work. A failed background restore is swallowed: it just leaves the session
  // unverified, since the interactive email/code screens (which call login() directly) surface
  // their own errors.
  init = async (): Promise<void> => {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this.loadKnoxersAddresses();

    if (!this.hasMatchingStoredRegistrationContext()) {
      // Either this registration can't be identified (no ref/asyncId), or whatever credential is
      // sitting in the shared auth_guest storage belongs to a different registration link on
      // this same origin -- never silently reuse it. Over-clearing here is fine: worst case the
      // supplier re-verifies.
      this.clearCredentials();
      this.clearStoredRegistrationContext();
      return;
    }

    this.loadCredFromStorage();

    try {
      await this.login();
    } catch {
      // Swallowed by design -- see method comment above.
    }
  };

  // Drops the whole verified session -- in-memory state, stored credentials and the stored
  // registration context -- so the supplier can verify a different address. Needed after the
  // server rejects a with-documents submit with EMAIL_MISMATCH: the context-scoped restore would
  // otherwise re-verify the same mismatched email on every reload, and the attach gate (which
  // short-circuits on isVerified) would never reopen the verification modal.
  clearSession = (): void => {
    this.setLoggedIn(false);
    this._verifiedEmail.set(undefined);
    this._trustServerOverLocalClock = false;
    this.clearCredentials();
    this.clearStoredRegistrationContext();
  };

  private hasMatchingStoredRegistrationContext(): boolean {
    const currentContextKey = getCurrentRegistrationContextKey();

    if (!currentContextKey || typeof Storage === 'undefined') {
      return false;
    }

    return localStorage.getItem(SR_GUEST_SESSION_CONTEXT_STORAGE_KEY) === currentContextKey;
  }

  private storeCurrentRegistrationContext(): void {
    const currentContextKey = getCurrentRegistrationContextKey();

    if (!currentContextKey || typeof Storage === 'undefined') {
      return;
    }

    localStorage.setItem(SR_GUEST_SESSION_CONTEXT_STORAGE_KEY, currentContextKey);
  }

  private clearStoredRegistrationContext(): void {
    if (typeof Storage === 'undefined') {
      return;
    }

    localStorage.removeItem(SR_GUEST_SESSION_CONTEXT_STORAGE_KEY);
  }

  login = async (): Promise<void> => {
    if (!this.guestLoginCredentials) {
      return;
    }

    const { phoneToken, emailToken, ssoToken } = this.guestLoginCredentials;

    if (!((phoneToken || !config.shouldLoginWithPhone) && (emailToken || ssoToken))) {
      return;
    }

    try {
      this.setIsLoginLoading(true);
      // guestHeaderGenerator only emits Authorization-Guest once isLoggedIn is true, so this
      // has to be set optimistically before the exchange call, and rolled back on failure.
      this.setLoggedIn(true);

      const headers = await guestHeaderGenerator(this, null);

      const response = await request<GuestSessionResponse>(serverUrl, '/api/login/guest', {
        method: HttpMethod.get,
        dontRedirectToLogin: true,
        generateDynamicHeaders: false,
        headers,
        errorsHandler: {
          default: { message: 'Unexpected error occurred while trying to verify the email' },
          [HttpStatus.unauthorized]: { suppressNotification: true },
        },
      });

      const emailAttribute = response.attributes?.find((attribute) => attribute.type === 'Email');
      this._verifiedEmail.set(emailAttribute?.value ?? this.emailKnoxer?.email);
      this._trustServerOverLocalClock = this.isTokenExpiredByLocalClock();

      this.storeCredentials();
      this.storeCurrentRegistrationContext();
    } catch (e) {
      Log.event('supplierRegistrationGuestSessionService.login: error while exchanging the guest token', e);
      this.setLoggedIn(false);
      this._verifiedEmail.set(undefined);
      this._trustServerOverLocalClock = false;
      this.clearCredentials();
      this.clearStoredRegistrationContext();
      throw e;
    } finally {
      this.setIsLoginLoading(false);
    }
  };
}

export const guestSession = new SupplierRegistrationGuestSessionStore();
